// deriveLibraryRefs — the library-derived shared vocabulary: the rail's
// interactive lists, the search space a tab id resolves through, the composer's
// @-mention candidate pool, and the ambient-DTO resolver. Pure (no hooks, no
// I/O), split out of useShellFoundation (C1 #9).

import type { AmbientContextItem } from "../../components/Chat/types.js";
import type { ComposerResourceRef } from "../../components/Composer/PowerComposer/types.js";
import type {
    ExportRef,
    MaterialRef,
    ResourceId,
    ResourceRef,
    SourceRef,
} from "../../components/Navigation/types.js";
import type { AmbientItemDTO, Library } from "../ports.js";
import { refsOfLibrary, toComposerResourceRef } from "./constants.js";

export type LibraryRefs = {
    readonly allRefs: readonly ResourceRef[];
    readonly materialRefs: readonly MaterialRef[];
    readonly sourceItems: readonly SourceRef[];
    readonly packageItems: readonly ExportRef[];
    readonly refFor: (id: ResourceId) => ResourceRef | undefined;
    readonly resolveAmbientItems: (
        dto: readonly AmbientItemDTO[],
    ) => readonly AmbientContextItem[];
    readonly composerCandidates: readonly ComposerResourceRef[];
};

export const deriveLibraryRefs = (
    library: Library | null,
    untitled: string,
): LibraryRefs => {
    const allRefs: readonly ResourceRef[] = library
        ? refsOfLibrary(library, untitled)
        : [];
    const materialRefs = allRefs.filter(
        (r): r is MaterialRef => r.type === "material",
    );
    const sourceItems = allRefs.filter(
        (r): r is SourceRef => r.type === "source",
    );
    const packageItems = allRefs.filter(
        (r): r is ExportRef => r.type === "export",
    );
    const refFor = (id: ResourceId): ResourceRef | undefined =>
        allRefs.find((r) => r.id === id);
    // Resolve a port's `AmbientItemDTO[]` into displayable `AmbientContextItem[]`
    // — a `refFor` lookup + graceful-degradation `flatMap` (skip, never crash).
    // An ambient ref pointing at a resource not in the library index (a stale KB
    // triple, a since-deleted resource) is silently dropped.
    const resolveAmbientItems = (
        dto: readonly AmbientItemDTO[],
    ): readonly AmbientContextItem[] =>
        dto.flatMap((item) => {
            const ref = refFor(item.resourceId);
            return ref === undefined
                ? []
                : [
                      {
                          resourceRef: toComposerResourceRef(ref),
                          score: item.score,
                          relation: item.relation,
                      },
                  ];
        });
    // The composer's `@`-mention candidate pool — every resource in the
    // workspace's library, offered live (the SAME index the rail + omnibar
    // resolve tab ids through).
    const composerCandidates = allRefs.map(toComposerResourceRef);
    return {
        allRefs,
        materialRefs,
        sourceItems,
        packageItems,
        refFor,
        resolveAmbientItems,
        composerCandidates,
    };
};
